import { Route } from 'react-router';

import { EnsureRoleRoute } from '#bootstrap/utils';
import { ADDONS, PRICE_MODEL, BILLING_CYCLE } from '#common/constants/addons';
import { USER_ROLES } from '#common/constants';

import CouponList from './CouponList';
import CouponEdit from './CouponEdit';
import CouponBanner from './CouponBanner';
import CouponCheckout from './CouponCheckout';

import icon from './assets/SWMPromotion.jpg';

import type { Addon, AddonBootstrap, PanelItem } from '../types';

function Description() {
  return (
    <>
      <div>
        The <b>SWM</b> (S)mall (W)arehouse (M)anagement <b>Promotions Module</b> enables you to create and manage discount coupons for your customers:
      </div>
      <br />
      <div>
        Features:
        <ul>
          <li>Create <b>percentage</b> or <b>fixed amount</b> discount coupons</li>
          <li>Target discounts to <b>specific products</b>, <b>collections</b>, or the <b>entire cart</b></li>
          <li>Set <b>minimum purchase</b> requirements</li>
          <li>Assign coupons to <b>all customers</b> or <b>specific customers</b></li>
          <li>Configure <b>single-use</b> or <b>unlimited-use</b> coupons</li>
          <li>Set <b>validity periods</b> with start and end dates</li>
          <li><b>Stack multiple coupons</b> at checkout</li>
          <li>Automatic <b>coupon code generation</b></li>
        </ul>
      </div>
    </>
  );
}

function apply(bootstrap: AddonBootstrap) {
  // Add admin routes for coupon management
  bootstrap.admin = bootstrap.admin.concat([
    <Route
      key="route-admin-coupon"
      path="coupon"
    >
      <Route
        index
        element={(
          <EnsureRoleRoute roles={USER_ROLES.ADMIN_ONLY}>
            <CouponList />
          </EnsureRoleRoute>
        )}
      />
      <Route
        path="list"
        element={(
          <EnsureRoleRoute roles={USER_ROLES.ADMIN_ONLY}>
            <CouponList />
          </EnsureRoleRoute>
        )}
      />
      <Route
        path="edit"
        element={(
          <EnsureRoleRoute roles={USER_ROLES.ADMIN_ONLY}>
            <CouponEdit />
          </EnsureRoleRoute>
        )}
      />
    </Route>,
  ]);

  // Add to admin panel navigation after User Management
  const promotionsEntry: PanelItem = {
    label: 'Promotions',
    children: [{
      testid: 'coupon-list',
      label: 'Coupons',
      link: '/management/coupon/list',
      linkAlias: ['management/coupon', 'management/coupon/list'],
    }, {
      testid: 'coupon-add',
      label: 'Create Coupon',
      link: '/management/coupon/edit',
      linkAlias: ['management/coupon/edit'],
    }],
    roles: USER_ROLES.ADMIN_ONLY,
  };
  const userMgmtIndex = bootstrap.panel.findIndex((item) => item.label === 'User Management');
  if (userMgmtIndex !== -1) {
    bootstrap.panel = [
      ...bootstrap.panel.slice(0, userMgmtIndex + 1),
      promotionsEntry,
      ...bootstrap.panel.slice(userMgmtIndex + 1),
    ];
  } else {
    bootstrap.panel = bootstrap.panel.concat([promotionsEntry]);
  }
}

const info = {
  id: ADDONS.PROMOTIONS,
  available: true,
  name: 'SWM Promotions',
  version: '1.0',
  icon,
  descriptionText: 'Create and manage discount coupons for your customers with flexible targeting, validity periods, and usage rules.',
  descriptionComponent: <Description />,
  priceModel: PRICE_MODEL.CONTINUES_SUBSCRIPTION,
  prices: [{
    price: 29,
    cycle: BILLING_CYCLE.MONTHLY,
  }, {
    price: 290,
    cycle: BILLING_CYCLE.ANNUALLY,
  }],
};

// Export components for use in other parts of the application
export {
  CouponBanner,
  CouponCheckout,
};

const addon: Addon = {
  apply,
  info,
};

export default addon;
